import {
    Background,
    BackgroundColor,
    Container,
    ContainerType,
    Spacer,
    SpacerVariant,
    backgroundNeutralColorsOptions,
    spacerOptionsAll,
} from "../components/layout"
import Image, { AspectRatio } from "../Image"
import { Repeater, Text, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"

import { Color } from "../components/typography"
import { FAQRedesignProps } from "./Faq"
import React from "react"
import { useCheckForEmptyText } from "../../hooks/use-check-for-empty-text"

interface TextImageBlockRedesignProps {
    background: { color: BackgroundColor } | BackgroundColor
    pill: string
    text: TypographyElement[]
    spacerTop: SpacerVariant
    spacerBottom: SpacerVariant
    faq?: {
        id: string
        props: FAQRedesignProps
        type: "Faq Redesign"
    }[]
}

const TextImageBlockRedesign: types.Brick<TextImageBlockRedesignProps> = ({
    background,
    spacerTop,
    spacerBottom,
    pill,
}) => {
    const bgColor = typeof background === "object" ? background.color : background

    const emptyPill = useCheckForEmptyText(pill)

    return (
        <Background color={bgColor}>
            <Container type={ContainerType.Redesign}>
                <Spacer variant={spacerTop} />
                <div className="sm:grid sm:grid-cols-12 sm:gap-x-3.75 lg:gap-x-5">
                    <div className="col-span-10 col-start-2 flex flex-col justify-center md:col-span-6 md:col-start-1 lg:col-span-5 lg:col-start-1 xl:col-span-4 xl:col-start-2">
                        {!emptyPill && (
                            <div>
                                <Text
                                    propName="pill"
                                    placeholder="Label"
                                    renderBlock={({ children }) => (
                                        <span className="mb-5 inline-block rounded-full bg-orange px-5 pt-3 pb-2.5 text-base leading-none! font-semibold text-white xs:text-md lg:mb-6 lg:px-6 lg:pt-3.5 lg:pb-3 lg:text-lg">
                                            {children}
                                        </span>
                                    )}
                                />
                            </div>
                        )}
                        <Color>
                            <Typography
                                propName="text"
                                size="P1"
                                allowList={["S1", "link", "bold", "italic", "small", "sup", "ul", "cl"]}
                            />
                        </Color>
                    </div>
                    <div className="aspect-w-4 col-span-5 col-start-8 hidden overflow-hidden rounded-4xl aspect-h-5 md:block lg:rounded-5xl xl:col-start-7 xl:rounded-6xl">
                        <Image
                            propName="image"
                            aspectRatio={AspectRatio["4/5"]}
                            maxWidth={800}
                            className="w-full object-cover"
                        />
                    </div>
                </div>
                <div className="-mx-6.25 xs:-mx-10 sm:-mx-15 md:hidden">
                    <div className="aspect-w-1 mt-10 aspect-h-1 sm:hidden">
                        <Image
                            propName="image_mobile"
                            aspectRatio={AspectRatio["1/1"]}
                            maxWidth={767}
                            className="w-full object-cover"
                        />
                    </div>
                    <div className="aspect-w-3 mt-20 hidden aspect-h-2 sm:block">
                        <Image
                            propName="image_tablet"
                            aspectRatio={AspectRatio["3/2"]}
                            maxWidth={1023}
                            className="w-full object-cover"
                        />
                    </div>
                </div>
                <div className="mt-16 sm:mt-20 md:mt-28">
                    <Repeater propName="faq" itemProps={{ embedded: true } as FAQRedesignProps} />
                </div>
                <Spacer variant={spacerBottom} />
            </Container>
        </Background>
    )
}

TextImageBlockRedesign.schema = {
    name: "TextImageBlockRedesign",
    label: "Text Image Block Redesign",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/TextImageBlockRedesign.png`,
    repeaterItems: [
        {
            name: "faq",
            items: [
                {
                    type: "Faq Redesign",
                    label: "FAQ",
                    min: 0,
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: backgroundNeutralColorsOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
    getDefaultProps: (): TextImageBlockRedesignProps => ({
        background: { color: BackgroundColor.White },
        pill: "Lorem ipsum dolor",
        text: [
            {
                type: "h2",
                children: [{ text: "Lorem ipsum dolor sit amet", S1: true }],
            },
            {
                type: "cl",
                children: [
                    {
                        type: "cli",
                        children: [{ text: "Lorem ipsum dolor sit amet" }],
                    },
                    {
                        type: "cli",
                        children: [{ text: "Lorem ipsum dolor sit amet" }],
                    },
                    {
                        type: "cli",
                        children: [{ text: "Lorem ipsum dolor sit amet" }],
                    },
                    {
                        type: "cli",
                        children: [{ text: "Lorem ipsum dolor sit amet" }],
                    },
                ],
            },
        ],
        spacerTop: SpacerVariant.XL,
        spacerBottom: SpacerVariant.XL,
    }),
}

export default TextImageBlockRedesign
